import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Save, Send, Sparkles, Loader2, Download, Wallet } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import api from "../store/api";
import VipCountdown from "../components/vip/VipCountdown";
import { generateBoxInvoicePdf } from "../lib/invoicePdf";
import { applyCatalogControls, distinctColours, SORT_OPTIONS } from "../hooks/catalogSort";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const customerApi = axios.create({ baseURL: API });
customerApi.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("heritage-customer-token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

/**
 * Heritage HQ, VIP Build-A-Box.
 *
 * Customers pick varieties from the public catalogue.
 * Saves a draft → "Submit" locks it as `submitted` for the admin to allocate
 * stock against. No checkout: admin reviews → moves to active manual order.
 */
export default function VipBoxBuilder({ embedded = false } = {}) {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [box, setBox] = useState(null); // {size, variety_ids, ...}
  const [vipDisabled, setVipDisabled] = useState(false);
  const [size, setSize] = useState(6);
  const [pickedIds, setPickedIds] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [credits, setCredits] = useState(0);
  const [applyCredit, setApplyCredit] = useState(false);
  const [varietyRemaining, setVarietyRemaining] = useState({}); // {variety_id: tubers_left_in_programme}
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("az");
  const [colour, setColour] = useState("all");

  const colours = useMemo(() => distinctColours(catalog), [catalog]);
  const filteredCatalog = useMemo(
    () => applyCatalogControls(catalog, { q, sort, colour }),
    [catalog, q, sort, colour]
  );

  useEffect(() => {
    (async () => {
      try {
        const [bx, cat] = await Promise.all([
          customerApi.get("/customer/vip-box"),
          api.get("/catalog"),
        ]);
        setTiers(bx.data?.tiers || []);
        if (bx.data?.vip_disabled) { setVipDisabled(true); return; }
        setCatalog(cat.data || []);
        setCredits(Number(bx.data?.heritage_credits || 0));
        setVarietyRemaining(bx.data?.variety_remaining || {});
        if (bx.data?.box) {
          setBox(bx.data.box);
          setSize(bx.data.box.size);
          setPickedIds(bx.data.box.variety_ids || []);
        }
      } catch (e) {
        if (e?.response?.status === 401) navigate("/account/login");
        else toast.error("Couldn't load your VIP box.");
      }
    })();
  }, [navigate]);

  const tier = useMemo(() => tiers.find((t) => t.size === size) || { price: 0, shipping: 0, total: 0 }, [tiers, size]);
  const remaining = Math.max(0, size - pickedIds.length);
  const submitted = box?.status === "submitted" || box?.status === "invoiced" || box?.status === "awaiting_payment";
  const status = box?.status;
  // Countdown deadlines, populated by the backend (draft_expires_at on save, payment_due_at on submit)
  const draftDeadline = status === "draft" ? (box?.draft_expires_at || null) : null;
  const paymentDeadline = (status === "awaiting_payment" || status === "invoiced") ? (box?.payment_due_at || null) : null;
  const submittedDeadline = status === "submitted" ? (box?.draft_expires_at || null) : null;

  const togglePick = (id) => {
    if (submitted) return;
    setPickedIds((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= size) {
        toast.error(`Box is full, remove one before adding another.`);
        return cur;
      }
      return [...cur, id];
    });
  };

  const changeSize = (next) => {
    if (submitted) return;
    if (pickedIds.length > next) {
      if (!window.confirm(`Switching to a ${next}-pack will drop ${pickedIds.length - next} of your picks. Continue?`)) return;
      setPickedIds(pickedIds.slice(0, next));
    }
    setSize(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await customerApi.post("/customer/vip-box", { size, variety_ids: pickedIds });
      setBox(r.data);
      toast.success("Draft saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (remaining > 0) { toast.error(`Pick ${remaining} more first.`); return; }
    if (!window.confirm(`Submit your VIP box?\nYour invoice will be generated immediately and your tubers reserved for 48 hours while we await payment.`)) return;
    setSubmitting(true);
    try {
      await customerApi.post("/customer/vip-box", { size, variety_ids: pickedIds });
      const r = await customerApi.post("/customer/vip-box/submit", { apply_credit: applyCredit });
      setBox(r.data);
      setCredits((prev) => Math.max(0, prev - Number(r.data?.credit_applied || 0)));
      toast.success("Submitted, your invoice is ready. You have 48 hours to pay.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadInvoice = () => {
    try {
      const today = new Date();
      const due = new Date(today.getTime() + 48 * 3600 * 1000);
      const iso = (d) => d.toISOString().slice(0, 10);
      const seq = String(box?.id || "").slice(0, 4).toUpperCase();
      const invoiceNumber = `HD-VIP-${iso(today).replace(/-/g, "")}-${seq}`;
      const varietyNames = (box?.variety_ids || [])
        .map((vid) => (catalog.find((c) => c.id === vid) || {}).name)
        .filter(Boolean);
      const subtotal = Number(box?.price || 0) + Number(box?.shipping || 0);
      const creditApplied = Number(box?.credit_applied || 0);
      const total = Number(box?.total ?? Math.max(0, subtotal - creditApplied));
      generateBoxInvoicePdf({
        invoiceNumber,
        issueDate: iso(today),
        dueDate: iso(due),
        customer: { name: box?.customer_name, email: box?.customer_email },
        items: [{
          description: `VIP Build-a-Box , ${box?.size}-pack` +
            (varietyNames.length ? `\nIncludes: ${varietyNames.join(", ")}` : ""),
          qty: 1,
          unit_price: subtotal,
        }],
        subtotal,
        creditApplied,
        total,
        notes: box?.notes || "",
      });
      toast.success("Invoice downloaded.");
    } catch (e) {
      toast.error("Couldn't generate the invoice PDF.");
    }
  };

  return (
    <div className={embedded ? "" : "min-h-[100svh] bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)]"} data-testid="vip-box-page">
      {vipDisabled ? (
        <div className="grid place-items-center px-6 py-20" data-testid="vip-disabled-panel">
          <div className="max-w-md text-center bg-white border-2 border-[#6E1E3A]/20 p-10">
            <div className="font-label tracking-[0.4em] text-[11px] text-[#6E1E3A] uppercase">✦ VIP Build-a-Box ✦</div>
            <h1 className="font-display text-3xl text-[var(--dahlias-forest)] mt-3">By invitation only</h1>
            <p className="mt-4 text-sm text-[var(--dahlias-ink)]/70 italic leading-relaxed">
              VIP Build-a-Box isn't enabled on your account just now. If you think this is a mistake,
              send us a note via Contact Heritage in your HQ.
            </p>
          </div>
        </div>
      ) : (
      <>
      {!embedded && (
        <header className="bg-[var(--dahlias-paper)] border-b border-[#6E1E3A]/20 sticky top-0 z-20">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
            <Link to="/account" className="inline-flex items-center gap-2 text-sm text-[#6E1E3A] hover:underline" data-testid="vip-back">
              <ArrowLeft className="w-4 h-4" /> Back to Heritage HQ
            </Link>
            <span className="font-display tracking-[0.18em] uppercase text-sm text-[var(--dahlias-forest)]">VIP Build-A-Box</span>
          </div>
        </header>
      )}

      <main className={embedded ? "space-y-10" : "max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 space-y-10"}>
        <section>
          <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[#6E1E3A]">✦ Behind-the-curtain access ✦</span>
          <h1 className="mt-2 font-display text-4xl md:text-5xl text-[var(--dahlias-forest)] leading-[1.05]">
            Build your <span className="font-display-italic text-[#6E1E3A]">VIP box.</span>
          </h1>
          <p className="mt-3 text-sm md:text-base text-[var(--dahlias-ink)]/80 max-w-2xl">
            Pick {size === 6 ? "six" : "twelve"} dahlia varieties from the 2026 catalogue before public release on 1<sup>st</sup> August. Save a draft any time, your picks are held while we review.
          </p>
        </section>

        {/* Countdowns, draft 24h hold / submitted-awaiting-invoice / payment 48h window */}
        {draftDeadline && <VipCountdown deadline={draftDeadline} variant="draft" />}
        {submittedDeadline && <VipCountdown deadline={submittedDeadline} variant="submitted" />}
        {paymentDeadline && <VipCountdown deadline={paymentDeadline} variant="invoice" />}

        {/* Voucher redeem strip */}
        <VoucherRedeem onRedeemed={() => window.location.assign("/account?tab=orders")} />

        {/* Tier picker */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {tiers.map((t) => (
            <button
              key={t.size}
              type="button"
              onClick={() => changeSize(t.size)}
              data-testid={`vip-tier-${t.size}`}
              disabled={submitted}
              className={`text-left p-6 wobble-a border-2 transition shadow-sm ${
                size === t.size
                  ? "bg-[#6E1E3A] text-[#FBE6D8] border-[#6E1E3A]"
                  : "bg-white border-[#6E1E3A]/20 hover:border-[#6E1E3A]/60 text-[var(--dahlias-ink)]"
              } ${submitted ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <span className="font-label tracking-[0.4em] text-[10px] uppercase opacity-85">
                {t.size === 6 ? "Tasting" : "Collector"}
              </span>
              <div className="mt-2 font-display text-3xl">{t.label}</div>
              <div className="mt-2 text-sm opacity-90">
                <strong>${t.total.toFixed(0)}</strong> delivered · {t.size} heirloom tubers
              </div>
              {size === t.size && <Check className="w-5 h-5 mt-3" />}
            </button>
          ))}
        </section>

        {/* Heritage Credit balance + apply toggle */}
        {credits > 0 && !submitted && (
          <section
            data-testid="vip-credit-row"
            className="bg-[#6E1E3A]/8 border-2 border-[#6E1E3A]/30 wobble-a p-4 md:p-5 flex flex-wrap items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#6E1E3A] text-white">
                <Wallet className="w-4 h-4" />
              </span>
              <div>
                <div className="font-label tracking-[0.3em] text-[10px] uppercase text-[#6E1E3A]">
                  Heritage Credit balance
                </div>
                <div className="font-display text-xl text-[var(--dahlias-forest)]">
                  NZ${credits.toFixed(2)} available
                </div>
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-[var(--dahlias-ink)] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={applyCredit}
                onChange={(e) => setApplyCredit(e.target.checked)}
                data-testid="vip-apply-credit"
                className="w-4 h-4 accent-[#6E1E3A]"
              />
              Apply <strong className="text-[#6E1E3A]">NZ${Math.min(credits, Number(tier.total || 0)).toFixed(2)}</strong> to this box
            </label>
          </section>
        )}

        {/* Picker status */}
        <section data-testid="vip-status" className="bg-[#FBE6D8] border-2 border-[#6E1E3A]/30 wobble-a p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display text-2xl text-[var(--dahlias-forest)]">
              {pickedIds.length} / {size} picked · <span className="text-[#6E1E3A] font-display-italic">{remaining === 0 ? "Box is full!" : `${remaining} to go`}</span>
            </div>
            <div className="text-xs text-[var(--dahlias-ink)]/70 mt-0.5">
              {status === "awaiting_payment"
                ? "Submitted ✓ Your invoice is ready and your tubers are reserved for 48 hours pending payment."
                : submitted
                ? "Submitted ✓ Heritage will be in touch to confirm your selection."
                : "Tap a variety below to add or remove it from your box."}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={save} disabled={saving || submitted} data-testid="vip-save" className="inline-flex items-center gap-1.5 h-10 px-4 wobble-pill border-2 border-[#6E1E3A]/50 text-[#6E1E3A] hover:bg-[#6E1E3A]/10 text-xs font-label tracking-[0.3em] uppercase disabled:opacity-40">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save draft
            </button>
            <button type="button" onClick={submit} disabled={submitting || submitted || remaining > 0} data-testid="vip-submit" className="inline-flex items-center gap-1.5 h-10 px-5 wobble-pill bg-[#6E1E3A] hover:bg-[#8a2548] text-[#FBE6D8] text-xs font-label tracking-[0.3em] uppercase disabled:opacity-40">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {submitted ? "Submitted" : "Submit box"}
            </button>
            {submitted && (
              <button type="button" onClick={downloadInvoice} data-testid="vip-download-invoice" className="inline-flex items-center gap-1.5 h-10 px-4 wobble-pill bg-[var(--dahlias-forest)] hover:bg-[var(--dahlias-sage-deep)] text-[#FBE6D8] text-xs font-label tracking-[0.3em] uppercase">
                <Download className="w-3.5 h-3.5" /> Download invoice
              </button>
            )}
          </div>
        </section>

        {/* Catalogue grid */}
        <section>
          <h2 className="font-display text-2xl text-[var(--dahlias-forest)] mb-4">The 2026 collection</h2>
          {catalog.length === 0 ? (
            <p className="text-sm text-[var(--dahlias-ink)]/65 italic">
              Heritage is preparing the catalogue. Check back soon, your VIP box waits.
            </p>
          ) : (
            <>
            <div className="mb-4 flex flex-wrap gap-2 items-center">
              <div className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-[#6E1E3A]/15 flex-1 min-w-[180px] max-w-sm wobble-pill">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search varieties…"
                  data-testid="vip-catalog-search"
                  className="flex-1 bg-transparent outline-none text-sm text-[var(--dahlias-ink)]"
                />
              </div>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="vip-catalog-sort"
                className="px-3 py-2 text-sm bg-white border-2 border-[#6E1E3A]/15 text-[var(--dahlias-ink)] wobble-pill"
              >
                {SORT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
              <select
                value={colour}
                onChange={(e) => setColour(e.target.value)}
                data-testid="vip-catalog-colour"
                className="px-3 py-2 text-sm bg-white border-2 border-[#6E1E3A]/15 text-[var(--dahlias-ink)] wobble-pill max-w-full"
              >
                <option value="all">All colours</option>
                {colours.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredCatalog.map((v) => {
                const picked = pickedIds.includes(v.id);
                // varietyRemaining is keyed by varieties that have been taken;
                // missing key = 4 still available (full cap).
                const remainingPool = varietyRemaining[v.id] !== undefined
                  ? varietyRemaining[v.id]
                  : 4;
                const soldOut = remainingPool <= 0 && !picked;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => { if (!soldOut) togglePick(v.id); }}
                    data-testid={`vip-variety-${v.id}`}
                    disabled={submitted || soldOut}
                    className={`group relative text-left wobble-a overflow-hidden border-2 transition ${
                      picked
                        ? "border-[#6E1E3A] shadow-lg ring-2 ring-[#6E1E3A]/30"
                        : soldOut
                          ? "border-[#9C8B73]/30 grayscale opacity-60 cursor-not-allowed"
                          : "border-[#6E1E3A]/15 hover:border-[#6E1E3A]/50 shadow-sm"
                    } ${submitted && !picked ? "opacity-50" : ""}`}
                  >
                    <div className="aspect-square bg-gradient-to-br from-[#F4C8D2] via-[#C9508A] to-[#6E1E3A] relative">
                      {v.image && (
                        <img src={v.image} alt={v.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                      )}
                      {!v.image && (
                        <div className="absolute inset-0 grid place-items-center text-[#FBE6D8]/80">
                          <Sparkles className="w-8 h-8" />
                        </div>
                      )}
                      {picked && (
                        <div className="absolute top-2 right-2 w-8 h-8 wobble-blob bg-[#6E1E3A] text-[#FBE6D8] grid place-items-center shadow-lg">
                          <Check className="w-4 h-4" />
                        </div>
                      )}
                      {soldOut && (
                        <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#3C2A1E] text-[#FBE6D8] text-[9px] tracking-[0.25em] uppercase rounded" data-testid={`vip-soldout-${v.id}`}>
                          Sold out
                        </div>
                      )}
                    </div>
                    <div className="p-3 bg-white">
                      <div className="font-display text-base text-[var(--dahlias-forest)] leading-tight">{v.name}</div>
                      {(v.colour || v.bloom_size) && (
                        <div className="text-[11px] text-[#6E1E3A]/80 mt-0.5 font-display-italic">
                          {[v.colour, v.bloom_size].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      <div className="text-[10px] tracking-[0.25em] uppercase mt-1 text-[#6E1E3A]/70" data-testid={`vip-stock-${v.id}`}>
                        {soldOut ? "0 available" : `${remainingPool} available`}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            </>
          )}
        </section>
      </main>
      </>
      )}
    </div>
  );
}

/* ---------------- Voucher redeem ---------------- */

function VoucherRedeem({ onRedeemed }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await customerApi.post("/customer/vouchers/redeem", { code: code.trim().toUpperCase() });
      toast.success(`Voucher redeemed, your free ${r.data?.voucher?.size}-pack is ready in My orders.`);
      setCode("");
      if (onRedeemed) onRedeemed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't redeem code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="voucher-redeem"
      className="bg-[#FBE6D8] border-2 border-[#6E1E3A]/30 wobble-a p-5 md:p-6"
    >
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[#6E1E3A]">
            ✦ Got a voucher code? ✦
          </span>
          <h2 className="mt-1 font-display text-2xl text-[var(--dahlias-forest)] leading-tight">
            Redeem a <span className="font-display-italic text-[#6E1E3A]">Build-a-Box.</span>
          </h2>
        </div>
        <form onSubmit={submit} className="flex items-center gap-2 flex-wrap" data-testid="voucher-form">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="HRTG-XXXXXX"
            disabled={busy}
            data-testid="voucher-input"
            className="h-11 px-4 wobble-pill bg-white border-2 border-[#6E1E3A]/40 text-[#6E1E3A] font-mono tracking-wider focus:outline-none focus:border-[#6E1E3A]"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="voucher-submit"
            className="h-11 px-5 wobble-pill bg-[#6E1E3A] hover:bg-[#8a2548] text-[#FBE6D8] text-xs font-label tracking-[0.3em] uppercase disabled:opacity-40"
          >
            {busy ? "Redeeming…" : "Redeem"}
          </button>
        </form>
      </div>
    </section>
  );
}
